import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDeliverySettings, putDeliverySettings } from '@/services/catalog';
import { hasCapability } from '@/lib/capabilities';
import { paiseToRupees, rupeesToPaise } from '@/lib/money';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ChevronLeft, Bike } from 'lucide-react';

const NumField = ({ label, hint, testid, prefix, suffix, value, onChange }) => (
  <div>
    <Label className="text-sm font-semibold">{label}</Label>
    {hint && <p className="text-xs text-slate-400 mb-1">{hint}</p>}
    <div className="flex items-center gap-1 mt-1">
      {prefix && <span className="text-slate-500 text-sm">{prefix}</span>}
      <Input type="number" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="max-w-[160px]" />
      {suffix && <span className="text-slate-500 text-sm">{suffix}</span>}
    </div>
  </div>
);

export default function DeliverySettings() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');
  const storeId = user?.store_id;
  const canConfig = hasCapability(user, 'delivery.config');

  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.id) { navigate('/login'); return; }
    if (!canConfig) { toast.error('No permission for delivery settings'); navigate('/pos-dashboard'); return; }
    load();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    const res = await getDeliverySettings(storeId);
    const d = res.data;
    setCfg({
      enabled: d.enabled ?? true,
      base_fee: paiseToRupees(d.base_fee || 0),
      per_km_fee: paiseToRupees(d.per_km_fee || 0),
      free_radius_km: d.free_radius_km || 0,
      max_radius_km: d.max_radius_km ?? '',
      free_above: d.free_above == null ? '' : paiseToRupees(d.free_above),
      min_order_value: paiseToRupees(d.min_order_value || 0),
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await putDeliverySettings(storeId, {
        enabled: cfg.enabled,
        base_fee: rupeesToPaise(cfg.base_fee),
        per_km_fee: rupeesToPaise(cfg.per_km_fee),
        free_radius_km: parseFloat(cfg.free_radius_km) || 0,
        max_radius_km: cfg.max_radius_km === '' ? null : parseFloat(cfg.max_radius_km),
        free_above: cfg.free_above === '' ? null : rupeesToPaise(cfg.free_above),
        min_order_value: rupeesToPaise(cfg.min_order_value),
      }, token);
      toast.success('Delivery settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div className="p-8 text-center">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="delivery-settings-page">
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-30">
        <Button variant="ghost" size="sm" onClick={() => navigate('/pos-dashboard')} data-testid="ds-back"><ChevronLeft className="w-4 h-4" /></Button>
        <div className="flex items-center gap-2">
          <Bike className="w-5 h-5 text-sspice" />
          <div>
            <h1 className="font-heading text-lg font-bold text-slate-900">Delivery Settings</h1>
            <p className="text-xs text-slate-500">Configuration-driven delivery charges for {user?.store_name || 'this store'}</p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-5">
        <div className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-xl">
          <div>
            <p className="font-semibold text-slate-900">Delivery enabled</p>
            <p className="text-xs text-slate-400">Turn off to disable delivery orders store-wide</p>
          </div>
          <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="ds-enabled" />
        </div>

        <div className="p-4 bg-white border border-slate-200 rounded-xl space-y-4">
          <NumField label="Base delivery fee" hint="Flat charge applied to every delivery order" testid="ds-base-fee" prefix="₹" value={cfg.base_fee} onChange={(v) => setCfg({ ...cfg, base_fee: v })} />
          <NumField label="Per-km charge" hint="Added for each km beyond the free radius" testid="ds-per-km" prefix="₹" suffix="/ km" value={cfg.per_km_fee} onChange={(v) => setCfg({ ...cfg, per_km_fee: v })} />
          <NumField label="Free radius" hint="Distance covered by the base fee before per-km applies" testid="ds-free-radius" suffix="km" value={cfg.free_radius_km} onChange={(v) => setCfg({ ...cfg, free_radius_km: v })} />
          <NumField label="Delivery radius" hint="Max distance you deliver to (blank = unlimited)" testid="ds-max-radius" suffix="km" value={cfg.max_radius_km} onChange={(v) => setCfg({ ...cfg, max_radius_km: v })} />
          <NumField label="Free delivery above" hint="Waive delivery fee when subtotal reaches this (blank = never)" testid="ds-free-above" prefix="₹" value={cfg.free_above} onChange={(v) => setCfg({ ...cfg, free_above: v })} />
          <NumField label="Minimum order value" hint="Delivery orders must meet this subtotal" testid="ds-min-order" prefix="₹" value={cfg.min_order_value} onChange={(v) => setCfg({ ...cfg, min_order_value: v })} />
        </div>

        <p className="text-xs text-slate-400">Zone-wise pricing and time-slot charges are supported by the engine and can be enabled later without changing existing rules.</p>

        <Button className="w-full bg-sspice hover:bg-sspice-hover" onClick={save} disabled={saving} data-testid="ds-save">
          {saving ? 'Saving…' : 'Save Delivery Settings'}
        </Button>
      </div>
    </div>
  );
}
